import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo} from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {AnalyticsEventMetadata} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import {
  ActionOrigin,
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
  CSV_TYPE,
} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StudyDesignType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  allowPlateMaps,
  allowPlateRecords,
  enablePlateDesignContentsLayer,
} from 'notebook/integrations/plate-maps/FixedPlateEditorUtil';
import PlateDesignSummaryActionButton from 'notebook/integrations/plate-maps/plate-design/action-buttons/PlateDesignSummaryActionButton';
import {
  PlateDesign,
  PlateDesignStatus,
} from 'notebook/integrations/plate-maps/plate-design/NoteLinkedPlateDesignTypes';
import {
  getPlateDesignAsPlateMap,
  getPlateDesignName,
  getPlateDesignStalenessValidation,
} from 'notebook/integrations/plate-maps/plate-design/NoteLinkedPlateDesignUtil';
import PlateMapContainer from 'notebook/integrations/plate-maps/PlateMapContainer';
import ParametersForPlateMapWrapper from 'notebook/integrations/procedures/ParametersForPlateMapWrapper';
import {downloadNoteLinkedData} from 'notebook/notes/note-linked-objects/NoteLinkedDataDAO';
import {SimpleDownloadAsCSVButton} from 'notebook/tables/export/DownloadAsCSVButton';
import isConfiguration from 'notebook/util/isConfiguration';
import notifications from 'notifications';
import {
  InitiatingPlateDesignState,
  PlateDesignState,
  PlateMapType,
} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {
  PlateDesignVersionRef,
  SupportedFrozenDataParameterValue,
} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {
  getDisplayLayerDropdownOptionsForDataParameterValues,
  getDisplayLayerDropdownOptionsForFrozenValues,
  getDisplayLayerDropdownOptionsForLiveValues,
  getFrozenEntityChipData,
  getHydratedPlateData,
  getReagentAssignmentGroupsFromWells,
} from 'samples/plate-maps/plate-record/PlateRecordUtil';
import {
  PlateDataParameter,
  PlateMaterialInput,
  PlateMap,
  SchematizedAttributeType,
  SUPPORTED_FIELD_TYPES_FOR_ANNOTATION,
  SUPPORTED_FIELD_TYPES_FOR_DATA_PARAMETERS,
  PlateProcedureParameterType,
  DisplayLayerOptionSection,
} from 'samples/plate-maps/PlateMapConstants';
import PlateMapSidebarMetadata from 'samples/plate-maps/PlateMapSidebarMetadata';
import PlateMapSummary from 'samples/plate-maps/PlateMapSummary';
import {getStaleDataParameterFromFrozenValue} from 'samples/plate-maps/PlateMapUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useAsyncState, {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Spinner from 'ui-library/Spinner';
import APIUtil from 'util/APIUtil';
import {checkClipboardWriteAccess} from 'util/clipboard/ClipboardUtil';
import {ctrl} from 'util/Keyboard';
import {isServerRequestError} from 'util/Net';
import {assertNever} from 'util/Types';

export interface PlateDesignSummaryProps {
  plateDesign: PlateDesign;
  locked: boolean;
  viewer: boolean;
  fileId: string;
  openVersionDbId?: number;
  plateDesignVersionRef: PlateDesignVersionRef | null;
  noteLinkedPlateDesignUuid: string | undefined;
  onEdit: (initiatingPlateDesignState: InitiatingPlateDesignState) => void;
  onCreatePlateMap: (initiatingPlateDesignState: InitiatingPlateDesignState) => void;
  hasPlateRecords: boolean;
  getDesignClipboardItem: () => ClipboardItem;
  // Disable edit button if the plate design is protected by "Fill In Only" mode for entry.
  isProtected?: boolean;
  collapseId?: string;
}

interface InnerPlateDesignSummaryProps extends PlateDesignSummaryProps {
  isInProcedure: boolean;
  /* Design type of the associated procedure's study. This controls showing test order/dilution related
  information only for sample testing studies. */
  procedureStudyDesignType: StudyDesignType | null;
  plateMaterialInputs?: Array<PlateMaterialInput>;
  paramInfos: Array<PlateDataParameter> | null;
}

async function makePlateMap(plateDesign: PlateDesign): Promise<{
  plateMap: PlateMap;
  liveEntityChipData: {[id: string]: {name: string; iconId?: BenchlingIconName}};
}> {
  const {status} = plateDesign;
  switch (status) {
    case PlateDesignStatus.INITIAL: {
      const {schemaIdsByReagentId, unitsById, liveEntityChipData} = await getHydratedPlateData(
        plateDesign.data.plateMap.positions,
        'transfer'
      );
      return {
        plateMap: getPlateDesignAsPlateMap(plateDesign, schemaIdsByReagentId, unitsById),
        liveEntityChipData,
      };
    }
    case PlateDesignStatus.MODIFIED:
      return {plateMap: plateDesign.data.plateMap, liveEntityChipData: {}};
    default:
      assert(false, `Unexpected PlateDesignStatus: ${status}`);
  }
}

const disabledDueToSyncingTooltip = 'Disabled while design is saving';

function DownloadPlateDesignCSVButton({
  fileId,
  openVersionDbId,
  noteLinkedPlateDesignUuid,
  analyticsMetadata,
}: {
  fileId: string;
  openVersionDbId?: number;
  noteLinkedPlateDesignUuid: string | undefined;
  analyticsMetadata: AnalyticsEventMetadata;
}): JSX.Element {
  const disabled = noteLinkedPlateDesignUuid == null;

  return (
    <Tooltip text={disabled ? disabledDueToSyncingTooltip : 'Download as CSV'}>
      <SimpleDownloadAsCSVButton
        className={css(styles.downloadCsvButton)}
        onDownloadAsCSV={async () => {
          // The button is disabled if noteLinkedPlateDesignUuid is missing, so users shouldn't hit this path
          if (noteLinkedPlateDesignUuid == null) {
            throw new Error('Expected plate design to have a uuid.');
          }
          if (openVersionDbId == null) {
            throw new Error('Expected a version id.');
          }
          analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.DOWNLOADED_CSV, ANALYTICS_MODULE.SAMPLES, {
            ...analyticsMetadata,
            csvType: CSV_TYPE.PLATE_DESIGN,
          });
          const notificationId = _.uniqueId('note-linked-plate-design-');
          notifications.info('Downloading...', {id: notificationId, duration: 0});
          try {
            const res = await downloadNoteLinkedData({
              entryId: fileId,
              versionDbId: openVersionDbId,
              apiType: ModelTypeConstants.NOTE_LINKED_PLATE_DESIGN,
              uuid: noteLinkedPlateDesignUuid,
            });
            notifications.success('Download completed.', {id: notificationId});
            window.open(res!.url);
          } catch (e) {
            if (isServerRequestError(e)) {
              notifications.notifyError(e, {id: notificationId});
            } else {
              throw e;
            }
          }
        }}
        disabled={disabled}
        // For consistency, disable this tooltip in favor of the new style one.
        tooltipProps={{disabled: true}}
      />
    </Tooltip>
  );
}

function CopyDesignButton({
  disabled,
  plateDesignName,
  getDesignClipboardItem,
}: {
  disabled: boolean;
  plateDesignName: string;
  getDesignClipboardItem: () => ClipboardItem;
}): JSX.Element {
  return (
    <Tooltip
      text={
        disabled
          ? `Your browser does not allow Benchling to copy directly. Use ${ctrl}+C instead.`
          : 'Copy plate design'
      }
    >
      <Button
        size={ButtonSize.MINI}
        icon="copy"
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        onClick={async () => {
          if (!(await checkClipboardWriteAccess())) {
            return;
          }
          const data = getDesignClipboardItem();
          try {
            await navigator.clipboard.write([data]);
            // eslint-disable-next-line benchlint/always-throw-from-catch
          } catch (e) {
            notifications.error(`Failed to copy design to clipboard. Use ${ctrl}+C instead.`);
            return;
          }
          notifications.success(`Copied to clipboard: ${plateDesignName}`);
        }}
        disabled={disabled}
      />
    </Tooltip>
  );
}

/**
 * Create a combined list of data parameters from the document context and any additional data parameters
 * present on the plate map.
 *
 * This ensures that any parameter value in the plate design has a corresponding plate data parameter.
 * We prefer the parameters from the context and only add the constructed parameters when they are missing
 * from the context.
 */
function getPlateDataParameters(
  contextualDataParameters: Array<PlateDataParameter>,
  plateDesign: PlateDesign
): Array<PlateDataParameter> {
  const dataParametersById = Object.fromEntries(
    contextualDataParameters.map((dataParameter) => [dataParameter.versionId, dataParameter])
  );
  if (plateDesign.status === PlateDesignStatus.INITIAL) {
    const frozenParameterValuesOnPlate = plateDesign.data.plateMap.positions
      .flatMap((position) => position.dataParameterValues)
      .filter(
        (dataParameterValue): dataParameterValue is SupportedFrozenDataParameterValue =>
          dataParameterValue != null
      );
    frozenParameterValuesOnPlate.reduce((acc, frozenDataParameterValue) => {
      acc[frozenDataParameterValue.dataParameterVersionId] =
        acc[frozenDataParameterValue.dataParameterVersionId] ??
        getStaleDataParameterFromFrozenValue(frozenDataParameterValue);
      return acc;
    }, dataParametersById);
  } else if (plateDesign.status === PlateDesignStatus.MODIFIED) {
    const parametersOnPlate: Array<PlateDataParameter> = plateDesign.data.plateMap.wells.flatMap((well) =>
      Object.entries(well.dataParameterTags).map(([versionId, tag]) => {
        const dataParameterName =
          plateDesign.data.plateMap.dataParameterNamesByVersionId?.[versionId] ?? null;
        assert(
          dataParameterName != null,
          'Expected data parameter name to be present for each parameter on plate.'
        );
        return {
          inStep: false,
          name: dataParameterName,
          plannedValues: [],
          schemaField: tag.tagSchemaField,
          type: PlateProcedureParameterType.DATA_PARAMETER,
          versionId,
          outOfContext: true,
        };
      })
    );
    parametersOnPlate.reduce((acc, dataParameter) => {
      acc[dataParameter.versionId] = acc[dataParameter.versionId] ?? dataParameter;
      return acc;
    }, dataParametersById);
  } else {
    assertNever(plateDesign);
  }
  return Object.values(dataParametersById);
}

function InnerPlateDesignSummary({
  locked,
  isProtected,
  viewer,
  fileId,
  openVersionDbId,
  plateDesignVersionRef,
  noteLinkedPlateDesignUuid,
  plateDesign,
  isInProcedure,
  procedureStudyDesignType,
  plateMaterialInputs,
  paramInfos,
  onEdit,
  onCreatePlateMap,
  hasPlateRecords,
  getDesignClipboardItem,
  collapseId,
}: InnerPlateDesignSummaryProps): JSX.Element {
  const {currentState, makeCall} = useAsyncState({makeCall: makePlateMap});
  const analyticsMetadata = {
    actionOrigin: APIUtil.isDocument(fileId) ? ActionOrigin.WORKSHEET : ActionOrigin.NOTEBOOK,
    plateDesignId: plateDesignVersionRef?.plateDesignApiId,
    plateDesignVersionId: plateDesignVersionRef?.plateDesignVersionApiId,
    fileId,
  };

  const plateDataParameters = getPlateDataParameters(paramInfos ?? [], plateDesign);

  const dataParametersByVersionId = useMemo(
    () =>
      plateDataParameters.reduce<{[id: string]: PlateDataParameter}>((acc, plateDataParameter) => {
        acc[plateDataParameter.versionId] = plateDataParameter;
        return acc;
      }, {}),
    [plateDataParameters]
  );

  useEffect(() => {
    makeCall(plateDesign);
  }, [plateDesign, makeCall]);

  const wellSchemaOptions = useMemo(
    () =>
      plateDesign.status === PlateDesignStatus.INITIAL
        ? getDisplayLayerDropdownOptionsForFrozenValues(
            plateDesign.data.plateMap.positions.flatMap((position) => position.schemaFieldValues),
            SchematizedAttributeType.WELL_SCHEMA_FIELD,
            SUPPORTED_FIELD_TYPES_FOR_ANNOTATION
          )
        : getDisplayLayerDropdownOptionsForLiveValues(
            plateDesign.data.plateMap.wells
              .flatMap((x) => Object.entries(x.schemaTags))
              .map(([apiId, schemaTag]) => ({key: apiId, schemaTag})),
            SchematizedAttributeType.WELL_SCHEMA_FIELD,
            SUPPORTED_FIELD_TYPES_FOR_ANNOTATION
          ),
    [plateDesign.data.plateMap, plateDesign.status]
  );

  const plateDataParametersOptions = useMemo(
    () =>
      plateDesign.status === PlateDesignStatus.INITIAL
        ? getDisplayLayerDropdownOptionsForDataParameterValues(
            plateDesign.data.plateMap.positions.flatMap((position) => position.dataParameterValues ?? []),
            SchematizedAttributeType.DATA_PARAMETER,
            SUPPORTED_FIELD_TYPES_FOR_DATA_PARAMETERS
          )
        : getDisplayLayerDropdownOptionsForLiveValues(
            plateDesign.data.plateMap.wells
              .flatMap((x) => Object.entries(x.dataParameterTags))
              .map(([versionId, schemaTag]) => ({
                key: versionId,
                schemaTag,
                displayNameOverride:
                  dataParametersByVersionId[versionId]?.name ??
                  // If the plate has been pasted from another context, get the name from plate map.
                  plateDesign.data.plateMap.dataParameterNamesByVersionId?.[versionId] ??
                  null,
              })),
            SchematizedAttributeType.DATA_PARAMETER,
            SUPPORTED_FIELD_TYPES_FOR_DATA_PARAMETERS
          ),
    [dataParametersByVersionId, plateDesign.data.plateMap, plateDesign.status]
  );

  const selectedPlateDataParameterIds = new Set(
    plateDesign.status === PlateDesignStatus.INITIAL
      ? plateDesign.data.plateMap.positions
          .flatMap((position) => position.dataParameterValues ?? [])
          .map((dataParameterValue) => dataParameterValue.dataParameterVersionId)
      : plateDesign.data.plateMap.wells.flatMap((x) => Object.keys(x.dataParameterTags))
  );

  const frozenReagentAssignmentGroups = useMemo(
    () =>
      plateDesign.status === PlateDesignStatus.INITIAL
        ? getReagentAssignmentGroupsFromWells(plateDesign.data.plateMap.positions, 'transfer')
        : // If a plate design is modified, we don't have access to the backend-persisted assignment groups.
          // For now, we rely on `createAssignmentGroupsForAttribute` to construct them automagically.
          // The persisted values will be rendered from on page refresh.
          null,
    [plateDesign.data.plateMap, plateDesign.status]
  );
  const frozenEntityChipData = useMemo(
    () =>
      plateDesign.status === PlateDesignStatus.INITIAL
        ? getFrozenEntityChipData(
            plateDesign.data.plateMap.positions.map((position) => position.transfer),
            currentState.status === AsyncStatus.SUCCESS ? currentState.data.liveEntityChipData : {}
          )
        : // If a plate design is modified, we don't have access to the frozen chip data.
          // For now, we rely on live fetching in the modal.
          {},
    [plateDesign.data.plateMap, plateDesign.status, currentState]
  );

  const plateDesignName = getPlateDesignName(plateDesign);

  const isConfig = useMemo(() => isConfiguration(fileId), [fileId]);
  const canCreatePlateRecords = useMemo(() => allowPlateRecords({fileId, isConfig}), [fileId, isConfig]);
  const disabledSections = useMemo(
    () => (enablePlateDesignContentsLayer({fileId, isConfig}) ? [] : [DisplayLayerOptionSection.CONTENTS]),
    [fileId, isConfig]
  );

  // We optimistically render the plate design without waiting for the entry to actually save. This is indicated
  // by the presences of a plateDesignVersionId (which is cleared by the frontend on edit). When the entry is
  // eventually saved, the plate design version id from the backend is populated back into the cid map and
  // threaded through here.
  const isOptimisticRender = plateDesignVersionRef == null;

  const shouldDisableActionButtons =
    isOptimisticRender ||
    locked ||
    currentState.status !== AsyncStatus.SUCCESS ||
    (isInProcedure && paramInfos == null) ||
    !allowPlateMaps({fileId, isConfig});

  let editTooltipText: string | null = null;
  if (isProtected) {
    editTooltipText = '"Fill In Only" mode does not allow editing of plate designs.';
  } else if (hasPlateRecords) {
    editTooltipText =
      'Plate designs that have been used to create plates cannot be edited. Copy and paste a new design instead.';
  } else if (isOptimisticRender) {
    editTooltipText = disabledDueToSyncingTooltip;
  }

  const headerControls = !viewer ? (
    isOptimisticRender ? (
      <Spinner />
    ) : (
      <div className={css(styles.headerControls)}>
        <DownloadPlateDesignCSVButton
          fileId={fileId}
          noteLinkedPlateDesignUuid={noteLinkedPlateDesignUuid}
          openVersionDbId={openVersionDbId}
          analyticsMetadata={analyticsMetadata}
        />
        <CopyDesignButton
          disabled={navigator.clipboard.write == null} // If the browser doesn't support the copy API, disable
          plateDesignName={plateDesignName}
          getDesignClipboardItem={getDesignClipboardItem}
        />
        <PlateDesignSummaryActionButton
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          disabled={isProtected || shouldDisableActionButtons || hasPlateRecords}
          icon="pen"
          invalidPlateSchemaErrorText="Unable to edit plate design"
          plateDesign={plateDesign}
          onDoAction={async ({plateSchema}) => {
            if (shouldDisableActionButtons) {
              return;
            }
            const plateDesignState: PlateDesignState = {
              plateSchema,
              name: plateDesignName,
            };
            const {plateMap, plateMapStalenessValidation} = await getPlateDesignStalenessValidation(
              plateDesignVersionRef.plateDesignApiId
            );
            onEdit({
              plateDesignState,
              plateMap,
              plateMapStalenessValidation,
              selectedPlateDataParameterIds,
              wellSchemaOptions,
              plateDesignVersionRef,
              plateMapType: PlateMapType.DESIGN,
              plateDataParameters,
            });
          }}
          dataTestAttrs={{component: 'PlateDesignSummary', key: 'editButton'}}
          tooltipText={editTooltipText}
        />
        <PlateDesignSummaryActionButton
          buttonStyle={ButtonStyle.PRIMARY}
          buttonText="Fill plate"
          disabled={shouldDisableActionButtons || !canCreatePlateRecords}
          icon="fill-grid"
          invalidPlateSchemaErrorText="Unable to create plate record"
          plateDesign={plateDesign}
          onDoAction={async ({plateSchema}) => {
            if (shouldDisableActionButtons) {
              return;
            }
            const plateDesignState: PlateDesignState = {plateSchema};
            const {plateMap, plateMapStalenessValidation} = await getPlateDesignStalenessValidation(
              plateDesignVersionRef.plateDesignApiId
            );
            onCreatePlateMap({
              plateDesignState,
              plateMap,
              plateMapStalenessValidation,
              selectedPlateDataParameterIds,
              plateDesignVersionRef,
              wellSchemaOptions,
              plateMapType: PlateMapType.RECORD,
              plateDataParameters,
            });
          }}
          dataTestAttrs={{component: 'PlateDesignSummary', key: 'createRecordButton'}}
          tooltipText={
            canCreatePlateRecords
              ? isOptimisticRender
                ? disabledDueToSyncingTooltip
                : null
              : 'Creating plate records is not allowed in templates'
          }
        />
      </div>
    )
  ) : null;

  return (
    <PlateMapContainer
      header="Plate design"
      headerControls={headerControls}
      subheader={getPlateDesignName(plateDesign)}
      onHeaderContextMenu={noop}
      plateMapType={PlateMapType.DESIGN}
      collapseId={collapseId}
    >
      {currentState.status === AsyncStatus.SUCCESS ? (
        <PlateMapSummary
          plateMap={currentState.data.plateMap}
          plateDataParametersOptions={plateDataParametersOptions}
          wellSchemaOptions={wellSchemaOptions}
          frozenReagentAssignmentGroups={frozenReagentAssignmentGroups}
          procedureStudyDesignType={procedureStudyDesignType}
          plateMapType={PlateMapType.DESIGN}
          disabledSections={disabledSections}
          additionalMetadataContent={
            <PlateMapSidebarMetadata
              // TODO(BNCH-112173): Potentially add the created at date.
              metadata={[]}
              plateRecordDetailModalProps={{
                plateMapType: PlateMapType.DESIGN,
                plateMap: currentState.data.plateMap,
                plateDataParametersOptions,
                plateMaterialInputs,
                wellSchemaOptions,
                frozenReagentAssignmentGroups,
                frozenEntityChipData,
                procedureStudyDesignType: null,
                onRequestClose: noop,
              }}
              viewDetailsAnalyticsMetadata={analyticsMetadata}
            />
          }
        />
      ) : (
        // We could have better treatment for NOT_FETCHED / ERROR statuses.
        <CenteredHorizontalThrobber />
      )}
    </PlateMapContainer>
  );
}

export default function PlateDesignSummary(props: PlateDesignSummaryProps): JSX.Element {
  return (
    <div {...getTestProps({component: 'PlateDesignSummary', element: 'root'})}>
      <ParametersForPlateMapWrapper>
        {({isInProcedure, plateDataParameters, procedureStudyDesignType, plateMaterialInputs}) => (
          <InnerPlateDesignSummary
            {...props}
            plateMaterialInputs={plateMaterialInputs}
            isInProcedure={isInProcedure}
            procedureStudyDesignType={procedureStudyDesignType}
            paramInfos={plateDataParameters ?? null}
          />
        )}
      </ParametersForPlateMapWrapper>
    </div>
  );
}

const styles = StyleSheet.create({
  downloadCsvButton: {
    marginLeft: '0px',
  },
  headerControls: {
    display: 'flex',
    gap: '6px',
    minWidth: 'fit-content',
  },
});
